import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { operationType, selectDataType } from "../../../../../services/types";
import { Checkboxs } from "../../../../form/checkboxs";
import eventsJSON from './events.json';
import { updateNodeValue } from "../../../../../services/method";
import { Select } from "../../../../form/select";
import { TextInput } from "../../../../form/textInput";
import { Main, Styled } from "./style";
import { Switch } from "../../../../form/switch";
import { useDispatch, useSelector } from "react-redux";
import { reduxUpdateError } from "../../../../../redux/reducers/error";
import { RootState } from "../../../../../redux/store";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "../../../../form/pagination";
import { AuthenticateSelect } from "../../../../auth/select";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

let requests = { workspaces: false, spaces: false, folders: false, lists: false };

type ClickUpProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function ClickUp({ id, data, setChildMinimize, setChildSettings }: ClickUpProps) {
   const userId = useSelector((state: RootState) => state.user.data?._id);
   const dispatch = useDispatch();
   const [searchParams] = useSearchParams();
   const [actions, setActions] = React.useState<selectDataType[]>([
      { id: 1, name: 'Fetch attachments ', refId: 'fetch_attachments', isActive: true }, { id: 2, name: 'Watch changes', refId: 'watch_changes' },
      { id: 3, name: 'Update custom fields', refId: 'update_fields' }, { id: 4, name: 'Fill and update (Headers)', refId: 'fill_headers' },
      { id: 5, name: 'Create Task', refId: 'create_task' }, { id: 6, name: 'Watch Outputs', refId: 'watch_outputs' },
      { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' },
   ]);
   const { getNodes, getEdges, setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const credential = { headers: { Authorization: `Bearer ${data.value?.connection?.token}`}};
   const [isExpanded, setIsExpanded] = React.useState(false);

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => { 
      if (!data.value) {
         if (data.currentCategory === 'Actions') {
            setActions(actions => actions.map(action => ({ ...action, isActive: action.refId === 'update_fields' })));
         }
         updateNodeValue(id, setNodes, { 
            action: data.currentCategory === 'Actions' ? 'update_fields' : actions[0].refId, events: eventsJSON, workspaces: [], spaces: [], folders: [], lists: [], tasks: [],
            fetchTypes: [{ id: 1, name: 'List', refId: 'list', isActive: true }, { id: 2, name: 'Task', refId: 'task' }],
         });
      }
      else setActions(actions => actions.map(action => ({ ...action, isActive: action.refId === data.value.action }))); // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   React.useEffect(() => {
      if (requests.workspaces && data.value?.connection && data.value.workspaces.length) {
         const fetchData = async () => {
            requests.workspaces = false;
            const workspaceId = data.value.workspaces.find((item: any) => item.isActive).refId;
            updateNodeValue(id, setNodes, { spaces: [], folders: [], lists: [], tasks: [] });
            requests.spaces = true;
            dataReMap('spaces', (await axios.get(`/social_actions/clickup/spaces/${workspaceId}`, credential)).data);
         }
         fetchData();
      } // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.workspaces]);
   // ---
   React.useEffect(() => {
      if (requests.spaces && data.value?.connection && data.value.spaces.length) {
         const fetchData = async () => {
            requests.spaces = false;
            const spaceId = data.value.spaces.find((item: any) => item.isActive).refId;
            updateNodeValue(id, setNodes, { folders: [], lists: [], tasks: [] });
            requests.folders = true;
            dataReMap('folders', (await axios.get(`/social_actions/clickup/folders/${spaceId}`, credential)).data);
         }
         fetchData();
      } // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.spaces]);
   // ---
   React.useEffect(() => {
      if (requests.folders && data.value?.connection && data.value.folders.length) {
         const fetchData = async () => {
            requests.folders = false;
            const folderId = data.value.folders.find((item: any) => item.isActive).refId;
            updateNodeValue(id, setNodes, { lists: [], tasks: [] });
            requests.lists = true;
            dataReMap('lists', (await axios.get(`/social_actions/clickup/lists/${folderId}`, credential)).data);
         }
         fetchData();
      } // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.folders]);
   // ---
   React.useEffect(() => {
      if (requests.lists && data.value?.connection && data.value.lists.length) {
         const fetchData = async () => {
            requests.lists = false;
            const listId = data.value.lists.find((item: any) => item.isActive).refId;
            updateNodeValue(id, setNodes, { tasks: [] });
            const res = (await axios.get(`/social_actions/clickup/tasks/${listId}`, credential)).data;
            const tasks = [];
            
            for (let i = 1; i <= res.total_pages; i++) {
               if (i === 1) {
                  tasks.push(...res.tasks);
               } else {
                  const res = (await axios.get(`/social_actions/clickup/tasks/${listId}&page=${i}`, credential)).data;
                  tasks.push(...res.tasks);
               }
            }
            dataReMap('tasks', tasks);
         }
         fetchData();
      } // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.lists]);
   // ---
   const dataReMap = (type: string, data: any) => {
      const arr = data.map((item: any, index: number) => ({ id: index+1, name: item.name, refId: item.id, isActive: index === 0 }));
      updateNodeValue(id, setNodes, { [type]: arr }); return arr;
   }

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: { id: string, code: string, token: string }) => {
      if (type === 'add') {
         const workspaces = (await axios.get('/social_actions/clickup/workspaces', { headers: { Authorization: `Bearer ${data.token}`}})).data;
         requests.workspaces = true;
         updateNodeValue(id, setNodes, { connection: data, workspaces: dataReMap('workspaces', workspaces)});
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }
   // ---
   const handleSelectActions = (selectId: number) => {
      const refId = actions.find(obj => obj.id === selectId)?.refId;

      setActions((actions) => actions.map((action) => {
         if (action.id === selectId) return { ...action, isActive: true };
         else return { ...action, isActive: false };
      }));
      setNodes(nodes => nodes.map(node => {
         if (node.id === id) {
            return { ...node, data: { ...node.data, value: { ...node.data.value, action: refId }}}
         } else return node;
      }));
   }
   // ---
   const handleSelectItem = async (type: string, selectId: number) => {
      const arr = data.value[type].map((item: any) => ({ ...item, isActive: item.id === selectId })); // @ts-ignore
      requests[type] = true;
      updateNodeValue(id, setNodes, { [type]: arr });
   }
   // ---
   const handleCheckboxs = (type: 'events' | 'fields' | 'headers' | 'attachments', selectId: any, value?: boolean) => { 
      if (type === 'events') {
         updateNodeValue(id, setNodes, { events: data.value.events.map((item: any) => {
            if (item.id === selectId) return ({ ...item, isActive: !value })
            else return item;
         })});
      } else if (type === 'fields') {
         updateNodeValue(id, setNodes, { fields: data.value.fields.map((item: any) => ({ ...item, isActive: item.id === selectId ? !value : false }))});

      } else if (type === 'headers') {
         updateNodeValue(id, setNodes, { headersArr: data.value.headersArr.map((item: any) => {
            if (item.id === selectId) return ({ ...item, isActive: !value })
            else return item;
         })});

      } else if (type === 'attachments') {
         updateNodeValue(id, setNodes, { attachments: data.value.attachments.map((item: any) => {
            if (item.id === selectId) return ({ ...item, isActive: !value })
            else return item;
         })});
      }
   }
   // ---
   const handleButtons = async (type: 'webhook' | 'getFields' | 'attachments' | 'page' | 'download', value?: any) => {
      if (type === 'webhook') {
         const edges = getEdges();
         const isOutput = getNodes().some(node => node.data.action === 'output' && edges.some(edge => edge.target === node.id && edge.source === id));

         if (isOutput) {
            try {
               updateNodeValue(id, setNodes, { isLoading: true });
               const request = {
                  workspace_id: data.value.workspaces?.find((workspace: any) => workspace.isActive)?.refId,
                  endpoint: `https://api.dragify.ai/user/webhook?userId=${userId}&projectId=${searchParams.get('id')}&blockType=${data.action}&blockId=${id}&blockInx=${data.index}`,
                  events: data.value.events.filter((event: any) => event.isActive).map((event: any) => event.name),
                  space_id: data.value.spaces?.find((space: any) => space.isActive)?.refId,
                  folder_id: data.value.folders?.find((folder: any) => folder.isActive)?.refId,
                  list_id: data.value.lists?.find((list: any) => list.isActive)?.refId,
                  task_id: data.value.tasks?.find((task: any) => task.isActive)?.refId,
               }
               await axios.post('/social_actions/clickup/create_webhook', request, credential);
               updateNodeValue(id, setNodes, { isWebhook: true, isLoading: false });
            } catch {
               updateNodeValue(id, setNodes, { isLoading: false });
            }
         } else {
            dispatch(reduxUpdateError(`(${data.index}) There must be an output block connected.`));
         }

      } else if (type === 'getFields') {
         const listId = data.value.lists.find((item: any) => item.isActive)?.refId;
         if (!listId) return;
         const response = (await axios.get(`/social_actions/clickup/custom_fields/${listId}`, credential)).data.fields;
         updateNodeValue(id, setNodes, { fields: response.map((field: any, index: number) => ({ id: index+1, name: field.name, refId: field.id })) });

      } else if (type === 'attachments' || type === 'page') {
         try {
            const listId = data.value.lists?.find((task: any) => task.isActive)?.refId;
            if (!listId) return;

            updateNodeValue(id, setNodes, { isLoading: true });
            const edges = getEdges();
            const nodes = getNodes();
            const keywordsTargets: operationType[] = [];
            let keywords = '';

            for (const edge of edges) {
               if (edge.target === id) {
                  for (const node of nodes) { if (node.id === edge.source && edge.targetHandle === 'Input Value') keywordsTargets.push({ id: node.id, ...node.data }) }
               }
            }
            for (const item of keywordsTargets) {
               if (item.action === 'input' && item.value) keywords += ` ${item.value}`;
               else if (item.outputType === 'text' && item.executeRun) keywords += `${await item.executeRun({ id: item.id as string, nodes, setNodes, edges, isNeedValue: true, blockIdNeedValue: id })} `;
            }
            const taskName = isAttachmentsList ? '' : data.value.tasks?.find((task: any) => task.isActive)?.name || '';
            const response = (await axios.get(`/social_actions/clickup/fetch_task_attachments?list_id=${listId}&task_name=${taskName}&keywords=${keywords || data.value.keywords || ''}&page=${ type === 'page' ? value : 1}`, credential)).data;
            updateNodeValue(id, setNodes, { 
               isLoading: false, attachments: response.matching_attachments.map((file: any) => ({ id: file.id, name: file.name, url: file.url, type: file.mime_type, taskId: file.task_name })),
               page: { listId, taskName, current_page: response.current_page, total_pages: response.total_pages }
            });
         } catch {
            updateNodeValue(id, setNodes, { isLoading: false });
         }
      } else if (type === 'download') {
         window.open(data.value.attachments.find((file: any) => file.isActive).url, '_blank');
      }
   }
   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => { 
      let obj: any = {};
      if (keyRef === 'headers' && e.target.value?.trim()) {
         obj.headersArr = e.target.value.split(',').filter(f => f).map((item: string, index: number) => ({ id: index + 1, name: item.trim(), isActive: true })).filter(f => f);
      } // @ts-ignore
      updateNodeValue(id, setNodes, { [keyRef]: e.target.value, ...obj });
   };

   // ---
   const attachmentsLengthActive = data.value?.action === 'fetch_attachments' ? data.value?.attachments?.filter((item: any) => item.isActive)?.length : 0;
   const isAttachmentsList = data.value?.action === 'fetch_attachments' && data.value?.fetchTypes?.some((f: any) => f.refId === 'list' && f.isActive);
   const isFilesEdit = data.value?.action === 'watch_changes' || (data.value?.action === 'update_fields' && data.value?.fields?.length) || (data.value?.action === 'fetch_attachments' && data.value?.attachments?.length);
   const isWarning = !data.value?.connection;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            styleParent={ (isFilesEdit ? { right: '-136.5px' } : {}) }
            style={{ width: '420px' }}
         ><Styled>
            <AuthenticateSelect type="clickup" defaultId={ data.value?.connection?.id } handleAuthenticate={ handleAuthenticate } background={ data.background } />
            <div className="action">
               { data.value?.connection ?
                  data.value.action === 'watch_changes' && !data.value.isWebhook ? <button 
                     className="btn1 withBorder" 
                     onClick={ _=> handleButtons('webhook') }
                     disabled={ 
                        !data.value?.workspaces?.length || !data.value?.spaces?.length || !data.value?.folders?.length || 
                        !data.value?.lists?.length || !data.value?.tasks?.length 
                     }
                  >Create Webhook</button> :
                  data.value.action === 'update_fields' ? 
                     <button className="btn1 withBorder" onClick={ _=> handleButtons('getFields') }>Get custom fields</button> :
                  data.value.action === 'fetch_attachments' ? <>
                     <button className="btn1 withBorder" onClick={ _=> handleButtons('attachments') } disabled={ !data.value?.tasks?.length || data.value?.isLoading }>Fetch</button> 
                     { attachmentsLengthActive === 1 ? <button className="btn1 withBorder download" onClick={ _=> handleButtons('download') }>Download</button> : null }
                  </> : null
               : null }
            </div>
            <Select title="Request" data={ actions } handleSelectItem={ handleSelectActions } isSmall={ true } />
            <Select title="Workspace" data={ data.value?.workspaces } handleSelectItem={ (selectId: number) => handleSelectItem('workspaces', selectId) } isSmall={ true } />
            <Select title="Space Id" data={ data.value?.spaces } handleSelectItem={ (selectId: number) => handleSelectItem('spaces', selectId) } isSmall={ true } />
            <div className="scroll nowheel">
               <Select title="Folder Id" data={ data.value?.folders } handleSelectItem={ (selectId: number) => handleSelectItem('folders', selectId) } isSmall={ true } />
               <Select title="List Id" data={ data.value?.lists } handleSelectItem={ (selectId: number) => handleSelectItem('lists', selectId) } isSmall={ true } />
               { data.value?.action === 'create_task' ? <TextInput keyRef="task_type" value={ data.value?.task_type } title="Task Type" placeholder="task_type" handleChange={ handleChangeInput } /> : null }
               { !isAttachmentsList && data.value?.action !== 'create_task' ? <Select title="Task/Entity" data={ data.value?.tasks } handleSelectItem={ (selectId: number) => handleSelectItem('tasks', selectId) } isSmall={ true } /> : null }
               { data.value?.action === 'fill_headers' ? <>
                  <TextInput keyRef="headers" value={ data.value?.headers } title="Headers" placeholder="headers_value" handleChange={ handleChangeInput } />
                  { data.value?.headers ? <Checkboxs className="headers" data={ data.value?.headersArr } handelCheckbox={ (selectId, value) => handleCheckboxs('headers', selectId, value) } isSmall={ true } /> : null }
               </> : data.value?.action === 'watch_changes' ? <div className="filesEdit events">
                  <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Events ({ data.value?.events?.filter((item: any) => item.isActive)?.length }/{ data.value?.events?.length })</h3>
                  <div className="scroll nowheel">
                     <Checkboxs data={ data.value?.events || [] } handelCheckbox={ (selectId, value) => handleCheckboxs('events', selectId, value) } />
                  </div>
               </div> : 
               data.value?.action === 'update_fields' && data.value?.fields?.length ? <div className="filesEdit fields">
                  <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Fields ({ data.value?.fields?.length })</h3>
                  <div className="scroll nowheel">
                     <Checkboxs data={ data.value?.fields || [] } handelCheckbox={ (selectId, value) => handleCheckboxs('fields', selectId, value) } />
                  </div>
               </div> : 
               data.value?.action === 'fetch_attachments' ? <>
                  <Select title="Fetch with" data={ data.value?.fetchTypes || [] } handleSelectItem={ (selectId: number) => handleSelectItem('fetchTypes', selectId) } isSmall={ true } />
                  <TextInput keyRef="keywords" value={ data.value?.keywords } title="Keywords" placeholder="search_keywords" handleChange={ handleChangeInput } /> 
                  <Switch title="Start autopilot (with loop)" className="autopilot" value={ !!data.value?.isAutopilot } setValue={ () => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot }) } />
                  <p>Please select your keywords carefully to avoid downloading incorrect or unintended files!</p>
                  { data.value?.attachments?.length ? <div className="filesEdit fields">
                     <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Attachments ({ attachmentsLengthActive }/{ data.value.attachments.length })</h3>
                     <div className="scroll nowheel">
                        <Pagination
                           disabled={ data.value?.isLoading }
                           currentPage={ data.value.page?.current_page } 
                           totalPages={ data.value.page?.total_pages } 
                           onPageChange={ (number) => handleButtons('page', number) } 
                           color={ data.background }
                        />
                        <Checkboxs data={ data.value.attachments } handelCheckbox={ (selectId, value) => handleCheckboxs('attachments', selectId, value) } />
                     </div>
                  </div> : null }
               </> : null }
            </div>
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}